/**
 * Streuflaeche der Partikel-Effekte (UI "Spawn area"): Quadrat, Kreis, Herz,
 * Stern. Reine Mathematik ohne three/DOM — bewusst eigenes Modul, damit sie
 * sich ohne den Vite-Asset-Stack testen laesst (particles.ts zieht Fonts).
 */
/** Streuflaeche (UI "Spawn area"): wo Partikel sichtbar sind. */
export type SpawnShape = 'square' | 'round' | 'heart' | 'star'

// HERZ und STERN (Jans Wunsch 09.09.2026) als KONTUR-TABELLE: Randradius je
// Winkel um die Mitte, Einheit 1 = weitester Punkt. Beide Umrisse sind
// sternfoermig bezueglich ihrer Mitte (jeder Strahl trifft den Rand genau
// einmal), also reicht ein Vergleich "Abstand / Randradius in dieser
// Richtung" — dieselbe Groesse r wie beim Kreis, und die Ausblendung zum Rand
// (Edge Falloff) faellt fuer alle Formen gleich aus.
const KONTUR_SCHRITTE = 720

const konturTabelle = (umriss: Array<[number, number]>): Float32Array => {
  const tab = new Float32Array(KONTUR_SCHRITTE)
  const proben: Array<[number, number]> = []
  for (let i = 0; i < umriss.length; i++) {
    const a = umriss[i]
    const b = umriss[(i + 1) % umriss.length]
    for (let k = 0; k < 48; k++) {
      const u = k / 48
      proben.push([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u])
    }
  }
  let maxR = 0
  for (const [x, y] of proben) maxR = Math.max(maxR, Math.hypot(x, y))
  for (const [x, y] of proben) {
    const r = Math.hypot(x, y) / (maxR || 1)
    const idx =
      Math.round(((Math.atan2(y, x) + Math.PI) / (2 * Math.PI)) * KONTUR_SCHRITTE) %
      KONTUR_SCHRITTE
    if (r > tab[idx]) tab[idx] = r
  }
  // Winkel ohne Probe (sehr spitze Ecken): Nachbarwert uebernehmen
  for (let pass = 0; pass < 3; pass++) {
    for (let i = 0; i < KONTUR_SCHRITTE; i++) {
      if (!tab[i])
        tab[i] =
          tab[(i + KONTUR_SCHRITTE - 1) % KONTUR_SCHRITTE] ||
          tab[(i + 1) % KONTUR_SCHRITTE]
    }
  }
  return tab
}

// Herz: klassische Parameterkurve (Boegen oben, Spitze unten), Mitte etwas
// unter der Kerbe, damit jeder Strahl aus der Mitte den Rand nur einmal trifft.
const HERZ_KONTUR = konturTabelle(
  Array.from({ length: 256 }, (_, i) => {
    const t = (i / 256) * Math.PI * 2
    const x = 16 * Math.sin(t) ** 3
    const y =
      13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)
    return [x, y + 2] as [number, number]
  })
)

// Stern: fuenfzackig (Pentagramm-Umriss), eine Zacke nach oben,
// Innenradius des regelmaessigen Pentagramms = cos72/cos36.
const STERN_KONTUR = konturTabelle(
  Array.from({ length: 10 }, (_, i) => {
    const innen = Math.cos((72 * Math.PI) / 180) / Math.cos((36 * Math.PI) / 180)
    const r = i % 2 === 0 ? 1 : innen
    const w = Math.PI / 2 + (i * Math.PI) / 5
    return [r * Math.cos(w), r * Math.sin(w)] as [number, number]
  })
)

const konturRadius = (tab: Float32Array, x: number, y: number): number => {
  const a = ((Math.atan2(y, x) + Math.PI) / (2 * Math.PI)) * KONTUR_SCHRITTE
  const i0 = Math.floor(a) % KONTUR_SCHRITTE
  const i1 = (i0 + 1) % KONTUR_SCHRITTE
  const u = a - Math.floor(a)
  return tab[i0] * (1 - u) + tab[i1] * u || 1e-4
}

// Per-particle alpha for the optional round area + edge falloff. `x`/`y` are the
// particle's stage position; halfW/halfH are the field half-extents. Returns
// 1 (fully visible) in the centre, fading to 0 toward the edge; 'round' also
// clips everything outside the inscribed circle.

export const edgeAlphaFor = (
  x: number,
  y: number,
  shape: SpawnShape,
  falloff: number,
  halfW: number,
  halfH: number
): number => {
  const f = Math.min(Math.max(falloff, 0), 1)
  // Ohne Ausblendung keine Maske: der Effekt streut wie bisher ueber die ganze
  // Buehne. Erst ein Falloff > 0 begrenzt ihn auf die gewaehlte Streuflaeche.
  // (Nur beim Quadrat — Kreis, Herz und Stern beschneiden immer.)
  if (shape === 'square' && f <= 0) return 1
  // Beide Formen beziehen sich auf DIESELBE Groesse (kleinere Buehnenhaelfte):
  // der Kreis war ein Kreis, das "Rechteck" dagegen die volle Buehne. Dadurch
  // lagen fast alle Partikel im vollsichtbaren Innenbereich und die Ausblendung
  // war kaum wahrnehmbar — als Quadrat wirkt sie wie beim Kreis (Jans Fund).
  const R = Math.min(halfW, halfH) || 1
  let r: number
  if (shape === 'heart' || shape === 'star') {
    const px = x / R
    const py = y / R
    r = Math.hypot(px, py) / konturRadius(shape === 'heart' ? HERZ_KONTUR : STERN_KONTUR, px, py)
  } else {
    r =
      shape === 'round'
        ? Math.hypot(x, y) / R
        : Math.max(Math.abs(x), Math.abs(y)) / R
  }
  // ausserhalb der Flaeche unsichtbar. Vorher blieb das Rechteck aussen VOLL
  // sichtbar (return 1) — das hob die Ausblendung am Rand komplett auf.
  if (r >= 1) return 0
  const start = 1 - f // fade begins here
  if (r <= start) return 1
  return 1 - (r - start) / Math.max(1e-4, 1 - start)
}
